import React, { Fragment, useState, useContext, useEffect } from 'react';
import {
  Box,
  Container,
  Text,
  Heading,
  Button,
  Table,
  IconButton,
  Tr,
  Th,
  Td,
  Tbody,
  Thead,
  Center,
  Input,
  FormControl,
  FormLabel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  HStack,
  Textarea,
  Flex,
  Select,
  Badge,
  Image,
  VStack,
  Divider,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';

import { FaTrashAlt, FaPencilAlt, FaPlus, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { AddIcon, DragHandleIcon } from '@chakra-ui/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useMultiState } from '../lib/hooks';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import AuthContext from '../contexts/AuthContext';
import axios from 'axios';

// Queries
import coursesAllQuery from '../graphql/queries/coursesAll';
import subjectsQuery from '../graphql/queries/subjects';

// Mutations
import createCourseMutation from '../graphql/mutations/createCourse';
import updateCourseMutation from '../graphql/mutations/updateCourse';
import removeCourseMutation from '../graphql/mutations/removeCourse';
import createCourseItemMutation from '../graphql/mutations/createCourseItem';
import updateCourseItemMutation from '../graphql/mutations/updateCourseItem';
import removeCourseItemMutation from '../graphql/mutations/removeCourseItem';

const URL = process.env.REACT_APP_UPLOAD_IMAGE;

const ManageCoursePage = () => {
  const { t } = useTranslation(); // eslint-disable-line
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const { colorMode } = useColorMode();

  // Queries
  const { data: coursesData, refetch: refetchCourses } = useQuery(coursesAllQuery, {
    fetchPolicy: 'network-only',
  });
  const { data: subjectsData } = useQuery(subjectsQuery);

  // Mutations
  const [createCourse] = useMutation(createCourseMutation);
  const [updateCourse] = useMutation(updateCourseMutation);
  const [removeCourse] = useMutation(removeCourseMutation);
  const [createCourseItem] = useMutation(createCourseItemMutation);
  const [updateCourseItem] = useMutation(updateCourseItemMutation);
  const [removeCourseItem] = useMutation(removeCourseItemMutation);

  // State
  const [coursesState, setCoursesState] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSubjectToAdd, setSelectedSubjectToAdd] = useState('');

  // Disclosures
  const { isOpen: modalAddCourse, onOpen: openAddCourse, onClose: closeAddCourse } = useDisclosure();
  const { isOpen: modalEditCourse, onOpen: openEditCourse, onClose: closeEditCourse } = useDisclosure();
  const { isOpen: modalRemoveCourse, onOpen: openRemoveCourse, onClose: closeRemoveCourse } = useDisclosure();
  const { isOpen: modalManageItems, onOpen: openManageItems, onClose: closeManageItems } = useDisclosure();

  // Form States
  const initialCourse = { _id: '', name: '', description: '', photo: '', status: 'public', index: 0 };
  const [courseAddData, setCourseAddData] = useMultiState(initialCourse);
  const [courseEditData, setCourseEditData] = useMultiState(initialCourse);
  const [removeCourseData, setRemoveCourseData] = useState(null);
  const [newphotoData, setnewphotoData] = useMultiState({ data: null, base64: '' });

  // Auth guard
  useEffect(() => {
    if (user?.role !== 'ADMIN') {
      setTimeout(() => history.push('/login'), 100);
    }
  }, [user, history]);

  // Sync data
  useEffect(() => {
    if (coursesData?.courses) {
      setCoursesState(coursesData.courses);
    }
  }, [coursesData]);

  useEffect(() => {
    if (subjectsData?.subjects) {
      setAllSubjects(subjectsData.subjects);
    }
  }, [subjectsData]);

  // Photo upload handler
  const selectPhotoFile = (e) => {
    e.preventDefault();
    const file = e.currentTarget.files[0];
    setnewphotoData({ data: file });
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setnewphotoData({ base64: reader.result });
    };
  };

  // ========================
  // COURSE CRUD
  // ========================
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    if (!courseAddData?.name) {
      toast({ position: 'top', title: t('managecoursePage.toast.warningTitle'), description: t('managecoursePage.toast.courseNameRequired'), status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    try {
      let photoUrl = '';
      if (newphotoData?.base64) {
        const res = await axios.post(URL, { photo: newphotoData.base64 });
        photoUrl = res?.data?.url || '';
      }
      await createCourse({
        variables: {
          name: courseAddData.name,
          description: courseAddData.description,
          photo: photoUrl,
          index: parseInt(courseAddData.index) || 0,
          status: courseAddData.status || 'public',
        },
      });
      toast({ position: 'top', title: t('managecoursePage.toast.successTitle'), description: t('managecoursePage.toast.courseCreated'), status: 'success', duration: 4000, isClosable: true });
      closeAddCourse();
      setCourseAddData(initialCourse);
      setnewphotoData({ data: null, base64: '' });
      refetchCourses();
    } catch (err) {
      toast({ position: 'top', title: t('managecoursePage.toast.errorTitle'), description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const handleUpdateCourse = async (e) => {
    e.preventDefault();
    if (!courseEditData?.name) {
      toast({ position: 'top', title: t('managecoursePage.toast.warningTitle'), description: t('managecoursePage.toast.courseNameRequired'), status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    try {
      let photoUrl = courseEditData.photo;
      if (newphotoData?.base64) {
        const res = await axios.post(URL, { photo: newphotoData.base64 });
        photoUrl = res?.data?.url || courseEditData.photo;
      }
      await updateCourse({
        variables: {
          _id: courseEditData._id,
          name: courseEditData.name,
          description: courseEditData.description,
          photo: photoUrl,
          index: parseInt(courseEditData.index) || 0,
          status: courseEditData.status,
        },
      });
      toast({ position: 'top', title: t('managecoursePage.toast.successTitle'), description: t('managecoursePage.toast.courseUpdated'), status: 'success', duration: 4000, isClosable: true });
      closeEditCourse();
      setnewphotoData({ data: null, base64: '' });
      refetchCourses();
    } catch (err) {
      toast({ position: 'top', title: t('managecoursePage.toast.errorTitle'), description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const handleRemoveCourse = async () => {
    try {
      await removeCourse({ variables: { _id: removeCourseData?._id } });
      toast({ position: 'top', title: t('managecoursePage.toast.successTitle'), description: t('managecoursePage.toast.courseRemoved'), status: 'success', duration: 4000, isClosable: true });
      closeRemoveCourse();
      setRemoveCourseData(null);
      refetchCourses();
    } catch (err) {
      toast({ position: 'top', title: t('managecoursePage.toast.errorTitle'), description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  // ========================
  // COURSE ITEMS (Subject management within course)
  // ========================
  const handleAddSubjectToCourse = async () => {
    if (!selectedSubjectToAdd || !selectedCourse?._id) {
      toast({ position: 'top', title: t('managecoursePage.toast.warningTitle'), description: t('managecoursePage.toast.subjectSelectRequired'), status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    // Check if subject already in course
    const alreadyExists = selectedCourse?.courseItems?.some((item) => item.subject?._id === selectedSubjectToAdd);
    if (alreadyExists) {
      toast({ position: 'top', title: t('managecoursePage.toast.warningTitle'), description: t('managecoursePage.toast.subjectAlreadyExists'), status: 'warning', duration: 4000, isClosable: true });
      return;
    }
    try {
      const newIndex = selectedCourse?.courseItems?.length || 0;
      await createCourseItem({
        variables: {
          course: selectedCourse._id,
          subject: selectedSubjectToAdd,
          index: newIndex,
        },
      });
      toast({ position: 'top', title: t('managecoursePage.toast.successTitle'), description: t('managecoursePage.toast.subjectAdded'), status: 'success', duration: 3000, isClosable: true });
      setSelectedSubjectToAdd('');
      await refetchCourses();
      // Update selectedCourse with new data
      const updatedCourses = (await refetchCourses())?.data?.courses;
      if (updatedCourses) {
        const updated = updatedCourses.find((c) => c._id === selectedCourse._id);
        if (updated) setSelectedCourse(updated);
      }
    } catch (err) {
      toast({ position: 'top', title: t('managecoursePage.toast.errorTitle'), description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const handleRemoveSubjectFromCourse = async (courseItemId) => {
    try {
      await removeCourseItem({ variables: { _id: courseItemId } });
      toast({ position: 'top', title: t('managecoursePage.toast.successTitle'), description: t('managecoursePage.toast.subjectRemoved'), status: 'success', duration: 3000, isClosable: true });
      const updatedCourses = (await refetchCourses())?.data?.courses;
      if (updatedCourses) {
        const updated = updatedCourses.find((c) => c._id === selectedCourse._id);
        if (updated) setSelectedCourse(updated);
      }
    } catch (err) {
      toast({ position: 'top', title: t('managecoursePage.toast.errorTitle'), description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  const handleMoveSubjectInCourse = async (courseItemId, direction) => {
    const items = [...(selectedCourse?.courseItems || [])].sort((a, b) => a.index - b.index);
    const currentIdx = items.findIndex((item) => item._id === courseItemId);
    if (currentIdx < 0) return;

    const swapIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;

    try {
      // Swap indexes
      await updateCourseItem({ variables: { _id: items[currentIdx]._id, index: items[swapIdx].index } });
      await updateCourseItem({ variables: { _id: items[swapIdx]._id, index: items[currentIdx].index } });

      const updatedCourses = (await refetchCourses())?.data?.courses;
      if (updatedCourses) {
        const updated = updatedCourses.find((c) => c._id === selectedCourse._id);
        if (updated) setSelectedCourse(updated);
      }
    } catch (err) {
      toast({ position: 'top', title: t('managecoursePage.toast.errorTitle'), description: err.message, status: 'error', duration: 6000, isClosable: true });
    }
  };

  // Get subjects not already in the selected course
  const getAvailableSubjects = () => {
    if (!selectedCourse) return allSubjects;
    const usedSubjectIds = (selectedCourse?.courseItems || []).map((item) => item.subject?._id);
    return allSubjects.filter((s) => !usedSubjectIds.includes(s._id));
  };

  const openEditCourseModal = (course) => {
    setCourseEditData({
      _id: course._id,
      name: course.name,
      description: course.description || '',
      photo: course.photo || '',
      status: course.status || 'public',
      index: course.index || 0,
    });
    setnewphotoData({ data: null, base64: '' });
    openEditCourse();
  };

  const openManageItemsModal = (course) => {
    setSelectedCourse(course);
    setSelectedSubjectToAdd('');
    openManageItems();
  };

  return (
    <Fragment>
      <Container maxW="7xl" py={10}>
        <Flex justifyContent="space-between" alignItems="center" mb={6}>
          <Heading size="lg">{t('managecoursePage.title')}</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="teal" onClick={() => { setCourseAddData(initialCourse); setnewphotoData({ data: null, base64: '' }); openAddCourse(); }}>
            {t('managecoursePage.createCourse')}
          </Button>
        </Flex>

        {/* Courses Table */}
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg" boxShadow="md">
          <Table variant="simple">
            <Thead bg={colorMode === 'light' ? 'gray.100' : 'gray.700'}>
              <Tr>
                <Th>{t('managecoursePage.table.index')}</Th>
                <Th>{t('managecoursePage.table.photo')}</Th>
                <Th>{t('managecoursePage.table.courseName')}</Th>
                <Th>{t('managecoursePage.table.description')}</Th>
                <Th>{t('managecoursePage.table.status')}</Th>
                <Th>{t('managecoursePage.table.subjectCount')}</Th>
                <Th>{t('managecoursePage.table.manage')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {coursesState.length === 0 ? (
                <Tr>
                  <Td colSpan={7}>
                    <Center py={10}>
                      <Text color="gray.500">{t('managecoursePage.emptyCourses')}</Text>
                    </Center>
                  </Td>
                </Tr>
              ) : (
                coursesState.map((course, idx) => (
                  <Tr key={course._id} _hover={{ bg: colorMode === 'light' ? 'gray.50' : 'gray.600' }}>
                    <Td>{course.index ?? idx + 1}</Td>
                    <Td>
                      {course.photo ? (
                        <Image src={course.photo} boxSize="50px" objectFit="cover" borderRadius="md" />
                      ) : (
                        <Box boxSize="50px" bg="gray.200" borderRadius="md" />
                      )}
                    </Td>
                    <Td fontWeight="bold">{course.name}</Td>
                    <Td maxW="200px" isTruncated>{course.description || '-'}</Td>
                    <Td>
                      <Badge colorScheme={course.status === 'public' ? 'green' : 'red'}>
                        {course.status === 'public'
                          ? t('managecoursePage.statusPublic')
                          : t('managecoursePage.statusUnpublic')}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme="blue">{t('managecoursePage.subjectsCount', { count: course.courseItems?.length || 0 })}</Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <Tooltip label={t('managecoursePage.tooltips.manageSubjects')}>
                          <IconButton
                            icon={<DragHandleIcon />}
                            size="sm"
                            colorScheme="blue"
                            onClick={() => openManageItemsModal(course)}
                          />
                        </Tooltip>
                        <Tooltip label={t('managecoursePage.tooltips.edit')}>
                          <IconButton
                            icon={<FaPencilAlt />}
                            size="sm"
                            colorScheme="yellow"
                            onClick={() => openEditCourseModal(course)}
                          />
                        </Tooltip>
                        <Tooltip label={t('managecoursePage.tooltips.remove')}>
                          <IconButton
                            icon={<FaTrashAlt />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => { setRemoveCourseData(course); openRemoveCourse(); }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      </Container>

      {/* ======================== */}
      {/* Modal: Create Course */}
      {/* ======================== */}
      <Modal isOpen={modalAddCourse} onClose={closeAddCourse} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('managecoursePage.createModalTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('managecoursePage.form.courseName')}</FormLabel>
                <Input placeholder={t('managecoursePage.form.courseNamePlaceholder')} value={courseAddData.name} onChange={(e) => setCourseAddData({ name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.description')}</FormLabel>
                <Textarea placeholder={t('managecoursePage.form.descriptionPlaceholder')} value={courseAddData.description} onChange={(e) => setCourseAddData({ description: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.index')}</FormLabel>
                <Input type="number" value={courseAddData.index} onChange={(e) => setCourseAddData({ index: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.status')}</FormLabel>
                <Select value={courseAddData.status} onChange={(e) => setCourseAddData({ status: e.target.value })}>
                  <option value="public">{t('managecoursePage.courseStatusPublic')}</option>
                  <option value="unpublic">{t('managecoursePage.courseStatusUnpublic')}</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.photo')}</FormLabel>
                <Input type="file" accept="image/*" onChange={selectPhotoFile} />
                {newphotoData.base64 && (
                  <Image src={newphotoData.base64} maxH="150px" mt={2} borderRadius="md" />
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={closeAddCourse}>{t('managecoursePage.buttons.cancel')}</Button>
            <Button colorScheme="teal" onClick={handleCreateCourse}>{t('managecoursePage.buttons.create')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ======================== */}
      {/* Modal: Edit Course */}
      {/* ======================== */}
      <Modal isOpen={modalEditCourse} onClose={closeEditCourse} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('managecoursePage.editModalTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('managecoursePage.form.courseName')}</FormLabel>
                <Input placeholder={t('managecoursePage.form.courseNamePlaceholder')} value={courseEditData.name} onChange={(e) => setCourseEditData({ name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.description')}</FormLabel>
                <Textarea placeholder={t('managecoursePage.form.descriptionPlaceholder')} value={courseEditData.description} onChange={(e) => setCourseEditData({ description: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.index')}</FormLabel>
                <Input type="number" value={courseEditData.index} onChange={(e) => setCourseEditData({ index: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.status')}</FormLabel>
                <Select value={courseEditData.status} onChange={(e) => setCourseEditData({ status: e.target.value })}>
                  <option value="public">{t('managecoursePage.courseStatusPublic')}</option>
                  <option value="unpublic">{t('managecoursePage.courseStatusUnpublic')}</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('managecoursePage.form.photo')}</FormLabel>
                <Input type="file" accept="image/*" onChange={selectPhotoFile} />
                {(newphotoData.base64 || courseEditData.photo) && (
                  <Image src={newphotoData.base64 || courseEditData.photo} maxH="150px" mt={2} borderRadius="md" />
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={closeEditCourse}>{t('managecoursePage.buttons.cancel')}</Button>
            <Button colorScheme="teal" onClick={handleUpdateCourse}>{t('managecoursePage.buttons.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ======================== */}
      {/* AlertDialog: Remove Course */}
      {/* ======================== */}
      <AlertDialog isOpen={modalRemoveCourse} onClose={closeRemoveCourse} isCentered>
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>{t('managecoursePage.removeDialogTitle')}</AlertDialogHeader>
          <AlertDialogBody>
            {t('managecoursePage.removeDialogBody', { name: removeCourseData?.name || '' })}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={closeRemoveCourse}>{t('managecoursePage.buttons.cancel')}</Button>
            <Button colorScheme="red" onClick={handleRemoveCourse} ml={3}>{t('managecoursePage.buttons.remove')}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ======================== */}
      {/* Modal: Manage Subjects in Course */}
      {/* ======================== */}
      <Modal isOpen={modalManageItems} onClose={closeManageItems} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="start" spacing={1}>
              <Text>{t('managecoursePage.manageSubjectsTitle')}</Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                {t('managecoursePage.manageSubjectsSubtitle', { name: selectedCourse?.name || '' })}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Add Subject Section */}
            <Box mb={6} p={4} borderWidth="1px" borderRadius="lg" bg={colorMode === 'light' ? 'gray.50' : 'gray.700'}>
              <Text fontWeight="bold" mb={3}>{t('managecoursePage.addSubjectTitle')}</Text>
              <HStack>
                <Select
                  placeholder={t('managecoursePage.selectSubjectPlaceholder')}
                  value={selectedSubjectToAdd}
                  onChange={(e) => setSelectedSubjectToAdd(e.target.value)}
                  flex={1}
                >
                  {getAvailableSubjects().map((subj) => (
                    <option key={subj._id} value={subj._id}>
                      {subj.name} {subj.teacher ? `(${subj.teacher.firstname} ${subj.teacher.lastname})` : ''}
                    </option>
                  ))}
                </Select>
                <Button leftIcon={<FaPlus />} colorScheme="teal" onClick={handleAddSubjectToCourse}>
                  {t('managecoursePage.buttons.add')}
                </Button>
              </HStack>
            </Box>

            <Divider mb={4} />

            {/* Subject List in Course */}
            <Text fontWeight="bold" mb={3}>
              {t('managecoursePage.subjectsInCourseTitle', { count: selectedCourse?.courseItems?.length || 0 })}
            </Text>

            {(!selectedCourse?.courseItems || selectedCourse.courseItems.length === 0) ? (
              <Center py={8}>
                <Text color="gray.500">{t('managecoursePage.emptySubjects')}</Text>
              </Center>
            ) : (
              <VStack spacing={3} align="stretch">
                {[...(selectedCourse?.courseItems || [])]
                  .sort((a, b) => (a.index || 0) - (b.index || 0))
                  .map((item, idx) => (
                    <Box
                      key={item._id}
                      p={4}
                      borderWidth="1px"
                      borderRadius="lg"
                      bg={colorMode === 'light' ? 'white' : 'gray.600'}
                      boxShadow="sm"
                      _hover={{ boxShadow: 'md' }}
                      transition="all 0.2s"
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <HStack spacing={4} flex={1}>
                          <Badge colorScheme="purple" fontSize="md" px={3} py={1} borderRadius="md">
                            {idx + 1}
                          </Badge>
                          {item.subject?.photo && (
                            <Image src={item.subject.photo} boxSize="40px" objectFit="cover" borderRadius="md" />
                          )}
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{item.subject?.name || t('managecoursePage.unknownSubject')}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {item.subject?.teacher ? `${item.subject.teacher.firstname} ${item.subject.teacher.lastname}` : ''}
                            </Text>
                          </VStack>
                          <Badge colorScheme={item.subject?.status === 'public' ? 'green' : 'orange'} ml={2}>
                            {item.subject?.status
                              ? (item.subject.status === 'public'
                                ? t('managecoursePage.statusPublic')
                                : t('managecoursePage.statusUnpublic'))
                              : t('managecoursePage.notAvailable')}
                          </Badge>
                        </HStack>
                        <HStack spacing={1}>
                          <Tooltip label={t('managecoursePage.tooltips.moveUp')}>
                            <IconButton
                              icon={<FaArrowUp />}
                              size="sm"
                              variant="ghost"
                              isDisabled={idx === 0}
                              onClick={() => handleMoveSubjectInCourse(item._id, 'up')}
                            />
                          </Tooltip>
                          <Tooltip label={t('managecoursePage.tooltips.moveDown')}>
                            <IconButton
                              icon={<FaArrowDown />}
                              size="sm"
                              variant="ghost"
                              isDisabled={idx === (selectedCourse?.courseItems?.length || 0) - 1}
                              onClick={() => handleMoveSubjectInCourse(item._id, 'down')}
                            />
                          </Tooltip>
                          <Tooltip label={t('managecoursePage.tooltips.removeFromCourse')}>
                            <IconButton
                              icon={<FaTrashAlt />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleRemoveSubjectFromCourse(item._id)}
                            />
                          </Tooltip>
                        </HStack>
                      </Flex>
                    </Box>
                  ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={closeManageItems}>{t('managecoursePage.buttons.close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Fragment>
  );
};

export default ManageCoursePage;
